import { createElement, setText } from './dom';
import { setLabel } from './i18n';
import {
  MOVEMENT_ACTIONS,
  isMovementKey,
  movementKeyLabel,
  movementPreferences,
  movementPreset,
  saveMovementPreferences,
  type MovementAction,
} from '../controls/movementBindings';

export const MOVEMENT_LABELS: Readonly<Record<MovementAction, string>> = {
  forward: 'Move forward',
  backward: 'Move backward',
  turnLeft: 'Turn left',
  turnRight: 'Turn right',
  strafeLeft: 'Strafe left',
  strafeRight: 'Strafe right',
  run: 'Run / Boost',
  ascend: 'Ascend (fly mode)',
  descend: 'Descend (fly mode)',
};

function movementLayout(parent: HTMLElement) {
  const section = createElement('section', 'movement-settings', parent);
  const heading = createElement('h3', '', section);
  heading.id = 'movement-settings-heading';
  setText(heading, 'Movement keys');
  section.setAttribute('aria-labelledby', heading.id);
  const handedness = createElement('label', 'handedness-setting', section);
  const toggle = createElement('input', '', handedness);
  toggle.type = 'checkbox';
  toggle.id = 'left-handed';
  const label = createElement('span', '', handedness);
  setText(label, 'Left handed');
  setLabel(toggle, 'aria-label', 'Left handed');
  const list = createElement('div', 'movement-bindings', section);
  const feedback = createElement('p', 'movement-feedback', section);
  feedback.setAttribute('aria-live', 'polite');
  const reset = createElement('button', 'movement-reset', section);
  reset.type = 'button';
  setText(reset, 'Reset movement keys');
  return { section, toggle, list, feedback, reset };
}

class MovementSettings {
  private readonly elements: ReturnType<typeof movementLayout>;
  private readonly buttons = new Map<MovementAction, HTMLButtonElement>();
  private capturing: MovementAction | null = null;

  constructor(parent: HTMLElement) {
    this.elements = movementLayout(parent);
    for (const action of MOVEMENT_ACTIONS) this.addBinding(action);
    const { toggle, reset, section } = this.elements;
    toggle.addEventListener('change', () => {
      this.save(movementPreset(toggle.checked));
    });
    reset.addEventListener('click', () => {
      this.save(movementPreset(toggle.checked));
    });
    section.closest('dialog')?.addEventListener('close', () => {
      this.cancel();
    });
    this.render();
  }

  private addBinding(action: MovementAction): void {
    const row = createElement('div', 'movement-binding', this.elements.list);
    const label = createElement('span', '', row);
    label.id = `movement-${action}-label`;
    setText(label, MOVEMENT_LABELS[action]);
    const button = createElement('button', 'movement-key', row);
    button.type = 'button';
    button.dataset.action = action;
    button.id = `movement-${action}-key`;
    button.setAttribute('aria-labelledby', `${label.id} ${button.id}`);
    button.addEventListener('click', () => {
      this.cancel();
      this.capturing = action;
      setText(this.elements.feedback, '');
      this.render();
      this.elements.section.ownerDocument.addEventListener('keydown', this.capture, true);
    });
    this.buttons.set(action, button);
  }

  private render(): void {
    const preferences = movementPreferences();
    this.elements.toggle.checked = preferences.leftHanded;
    for (const [action, button] of this.buttons) {
      button.setAttribute('aria-pressed', String(this.capturing === action));
      setText(
        button,
        this.capturing === action ? 'Press a key' : movementKeyLabel(preferences.bindings[action])
      );
    }
  }

  private cancel(): void {
    this.capturing = null;
    this.elements.section.ownerDocument.removeEventListener('keydown', this.capture, true);
    this.render();
  }

  private save(next: ReturnType<typeof movementPreferences>): void {
    const saved = saveMovementPreferences(next);
    this.feedback(saved ? 'Keys saved' : 'These keys could not be saved in this browser.', !saved);
    this.cancel();
  }

  private feedback(message: string, error = false): void {
    this.elements.feedback.toggleAttribute('data-error', error);
    setText(this.elements.feedback, message);
  }

  private capture = (event: KeyboardEvent): void => {
    if (!this.capturing) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.code === 'Escape') {
      this.feedback('');
      this.cancel();
      return;
    }
    if (event.repeat) return;
    if (!isMovementKey(event.code) || event.metaKey || event.ctrlKey || event.altKey) {
      this.feedback('This key is reserved. Choose another key.', true);
      return;
    }
    const previous = movementPreferences();
    if (
      MOVEMENT_ACTIONS.some(
        (action) => action !== this.capturing && previous.bindings[action] === event.code
      )
    ) {
      this.feedback('This key is already assigned. Choose another key.', true);
      return;
    }
    this.save({ ...previous, bindings: { ...previous.bindings, [this.capturing]: event.code } });
  };
}

export function createMovementSettings(parent: HTMLElement): void {
  new MovementSettings(parent);
}
